import React, { useState, useEffect, useRef } from "react";

import RemoteSelect2 from "./remoteSelect2";
import {
  Button,
  Group,
  Title,
  Box,
  Text,
  Divider,
} from "@mantine/core";
import _ from "lodash";
import moment from "moment";
import { IconCircleCheck } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useServerApi } from "../hooks/userServerApi";
import { useTranslation } from "react-i18next";
import {
  getOrderProductPreQuery,
  getProductId,
} from "../helpers/orderProductUtils";

const ProductSelection = ({
  form,
  name,
  value,
  formAction,
  style,
  title,
  showProductSnapShot = false,
  onChange,
  size = "md",
  preQuery,
  filterByOrderType = false,
  ...props
}) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [outdated, setOutDated] = useState(false);
  const [latest, setLatest] = useState();
  const productSnapShot = _.get(form.values, "productSnapShot");
  const originalProductCaptured = useRef(false);

  useEffect(() => {
    if (originalProductCaptured.current) return;
    if (!form?.values?._id) return;
    const productId =
      getProductId(form.values.originalProductId) ||
      getProductId(form.values.product) ||
      getProductId(productSnapShot);
    if (!productId) return;
    originalProductCaptured.current = true;
    if (!form.values.originalProductId) {
      form.setFieldValue("originalProductId", productId);
    }
  }, [
    form?.values?._id,
    form?.values?.product,
    form?.values?.originalProductId,
    productSnapShot?._id,
  ]);

  const handleProductChange = (product) => {
    if (!product) {
      form.setFieldValue("productSnapShot", null);
      form.setFieldValue("mpfAccountType", undefined);
      form.setFieldValue("productCustomOptions", []);
      form.setFieldValue("commissionCustomFieldsMeta", []);
      if (onChange) onChange(product);
      return;
    }
    form.setFieldValue("productSnapShot", product);
    form.setFieldValue("productCustomOptions", []);
    form.setFieldValue("commissionCustomFieldsMeta", []);
    if (product?.category?.code !== "MPF") {
      form.setFieldValue("mpfAccountType", undefined);
    }
    if (onChange) onChange(product);
  };

  const clearSelected = () => {
    form.setFieldValue(name, null);
    form.setFieldValue("productSnapShot", null);
    form.setFieldValue("mpfAccountType", undefined);
    form.setFieldValue("productCustomOptions", []);
    form.setFieldValue("commissionCustomFieldsMeta", []);
    setOutDated(false);
  };

  const getLatestProductInfo = async () => {
    if (!productSnapShot?._id) return;

    let result = await api.getById({
      apiEntity: "product",
      id: productSnapShot._id,
    });

    setLatest(result);

    form.setFieldValue("productSnapShot", result);
    return;

    _.isEqual(result, productSnapShot) ? setOutDated(false) : setOutDated(true);

    // console.log(
    //     "getLatestProductInfo",
    //     result.updatedAt,
    //     productSnapShot.updatedAt
    // );
  };

  const updateSnapshot = () => {
    form.setFieldValue("productSnapShot", latest);
    setOutDated(false);
  };

  useEffect(() => {
    // console.log(productSnapShot);
    getLatestProductInfo();
  }, [productSnapShot?._id]);

  const orderType = form?.values?.type ?? "NEW";
  const effectivePreQuery = filterByOrderType
    ? getOrderProductPreQuery(orderType)
    : preQuery;

  const prevOrderTypeRef = useRef(orderType);
  useEffect(() => {
    if (!filterByOrderType) return;

    const previousType = prevOrderTypeRef.current;
    const currentType = orderType;
    prevOrderTypeRef.current = currentType;

    if (previousType !== "TRANSFER" || currentType !== "NEW") return;

    const status = productSnapShot?.status;
    if (status && status !== "ACTIVE") {
      clearSelected();
      showNotification({
        color: "yellow",
        title: t("Product unavailable"),
        message: t(
          "Selected product is not available for New Biz orders. Please choose an active product."
        ),
      });
    }
  }, [orderType, filterByOrderType, productSnapShot?.status]);

  return (
    <>
      {/* <ReactJson
        src={form.values.productSnapShot}
        collapsed
        style={{ background: "white" }}
      /> */}
      {title && <Text size={"md"}>{t(title)}</Text>}
      <RemoteSelect2
        key={filterByOrderType ? orderType : undefined}
        apiEntity="product"
        required={true}
        placeholder={t("Search Product By Name/ Code/ Chinese name")}
        valueField="_id"
        size={size}
        labelField="name"
        // labelRender={(data) =>
        // 	`[ ${data.provider.shortName ?? " -- "} ] : ${data.name} ${
        // 		data.cname
        // 	} ( ${data.code} )`
        // }
        labelRender={(data) =>
          `[ ${data.code} ] ${data.name}${data.cname ? ` ${data.cname}` : ""}`
        }
        multiple={false}
        clearable={true}
        // preQuery: { $and: [ {isConsultant : true}, {isActive: true} ] }
        // preQuery={{ status: "ACTIVE" }}
        searchFields={["name", "code", "cname"]}
        sort={{
          by: "code",
          order: "asc",
        }}
        value={value}
        form={form}
        formAction={formAction}
        name={name}
        onDataChange={handleProductChange}
        preQuery={effectivePreQuery}
        {...props}
      />
      {/* <Group mt={20}>
        <Button size="xs" color={"red"} onClick={clearSelected}>
          {t("Clear")}
        </Button>
      </Group> */}

      {showProductSnapShot && productSnapShot && (
        <Box mt={30}>
          <Divider mb={20} />
          <Title order={5} mb={10}>
            Product Snapshot
          </Title>

          {outdated && (
            <>
              <Text color="dimmed" size="xs">
                Snapshot Version:{" "}
                {moment(productSnapShot.updatedAt).format("lll")}
              </Text>
              <Text color="dimmed" size="xs">
                Latest Version: {moment(latest.updatedAt).format("lll")}
              </Text>
              <Button size="xs" mt={20} onClick={updateSnapshot}>
                Update Product Snapshot
              </Button>
            </>
          )}

          {!outdated && (
            <Group>
              <IconCircleCheck color="green" />
              <Text size="xs" color="dimmed">
                Current Snapshot is most updated product version.
              </Text>
            </Group>
          )}
        </Box>
      )}
    </>
  );
};

export default ProductSelection;
